import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  dateTimeFormatter,
  predictTypeFormatter,
} from "../../utils/tableDataFormatter";

import {
  getIdentifiedCIPs,
  updatedCleanings,
  proceedAnalyticsStatus,
} from "../../network/cipAPI";
import DeleteRenderer from "../../components/AddCIPAndShutdown/deleteCellRender";

import DupontDialog from "../../components/DupontDialog";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import InfoIconGreen from "../../assets/info-icon-green.svg";
import "./index.scss";
import { API_URLS } from "../../network/appUrls";
import AgTable from "../../components/AgTable";
import moment from "moment";
import DateTimeCellEditor from "../../components/AgTable/DateTimeCellEditor";

const basePath = API_URLS.basePath;

const IdentifyCIP = ({
  isIdentifiedCECS,
  setIdentifiedCECS,
  plantId,
  min_date,
  max_date,
  fileInfo,
  cecWarningMessage,
  setCecWarningMessage,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [minDateLimit, setMinDateLimit] = useState(null);
  const [maxDateLimit, setMaxDateLimit] = useState(null);
  const [isDisabled, setIsDisabled] = useState(true);

  const CEC_TABLE_COLUMNS = (t) => [
    {
      headerName: t("HistoricCipTableStrings.index"),
      valueGetter: (params) => (params.node ? params.node.rowIndex + 1 : ""),
      minWidth: 20,
      headerClass: "custom-Header-cip-user-table",
      sortable: false,
    },
    {
      headerName: t("HistoricCipTableStrings.start_date"),
      field: "cleaning_start_time",
      valueFormatter: dateTimeFormatter,
      minWidth: 150,
      headerClass: "custom-Header-cip-user-table",
      editable: (params) => params.data && params.data.is_tool_identified,
      sortable: false,
      cellEditor: DateTimeCellEditor,
      cellEditorParams: {
        min: minDateLimit
          ? moment(minDateLimit).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
        max: maxDateLimit
          ? moment(maxDateLimit).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
        minDate: minDateLimit,
        maxDate: maxDateLimit,
        timeIntervals: 5,
        dateFormat: "yyyy-MM-dd HH:mm:ss",
        showTimeSelect: true,
        placeholderText: "Select date and time",
      },
    },
    {
      headerName: t("HistoricCipTableStrings.end_date"),
      field: "cleaning_end_time",
      valueFormatter: dateTimeFormatter,
      minWidth: 150,
      headerClass: "custom-Header-cip-user-table",
      editable: (params) => params.data && params.data.is_tool_identified,
      sortable: false,
      cellEditor: DateTimeCellEditor,
      cellEditorParams: {
        min: minDateLimit
          ? moment(minDateLimit).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
        max: maxDateLimit
          ? moment(maxDateLimit).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
        minDate: minDateLimit,
        maxDate: maxDateLimit,
        timeIntervals: 5,
        dateFormat: "yyyy-MM-dd HH:mm:ss",
        showTimeSelect: true,
        placeholderText: "Select date and time",
      },
    },
    {
      headerName: t("HistoricCipTableStrings.type"),
      field: "cleaning_type",
      minWidth: 100,
      headerClass: "custom-Header-cip-user-table",
      editable: (params) => params.data && params.data.is_tool_identified,
      sortable: false,
      valueFormatter: (params) => predictTypeFormatter(params, t),
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: ["CIP", "CEB", "Shutdown"],
        valueListGap: 10,
        valueListMaxHeight: 180,
        valueListMaxWidth: 150,
      },
    },
    {
      headerName: t("HistoricCipTableStrings.is_tool_identified"),
      field: "cec_source",
      minWidth: 100,
      headerClass: "custom-Header-cip-user-table",
      valueGetter: (params) =>
        params.data && params.data.is_tool_identified ? "System" : "User",
      sortable: false,
    },
    {
      colId: "delete",
      lockPinned: true,
      pinned: "right",
      width: 50,
      headerName: t("ValueStrings.delete"),
      cellRenderer: DeleteRenderer,
    },
  ];

  const [showOverLayLoader, setShowOverLayLoader] = useState(false);

  const [rowData, setRowData] = useState([]);
  const [colDefs, setColDefs] = useState(CEC_TABLE_COLUMNS(t));

  useEffect(() => {
    const fetchLimit = async () => {
      try {
        setMinDateLimit(moment(min_date || fileInfo.min_date).toDate());
        setMaxDateLimit(
          moment(max_date || fileInfo.max_date)
            .add(1, "days")
            .toDate()
        );
        // }
      } catch (err) {}
    };

    fetchLimit();
  }, []);

  useEffect(() => {
    setColDefs(CEC_TABLE_COLUMNS(t));
  }, [t, minDateLimit, maxDateLimit]);

  // Fetch CIPs only when dialog is opened
  useEffect(() => {
    async function fetchCIPs() {
      try {
        setShowOverLayLoader(true);
        const data = await getIdentifiedCIPs();
        if (data && data.status === 200 && data?.data && data?.data.cleanings) {
          setRowData(data.data.cleanings);
        } else {
          setRowData([]);
        }
      } catch (err) {
        setRowData([]);
      } finally {
        setShowOverLayLoader(false);
      }
    }
    if (isIdentifiedCECS) fetchCIPs();
  }, [isIdentifiedCECS, t, minDateLimit, maxDateLimit]);

  const intervalId = React.useRef(null);

  const checkStatus = async () => {
    try {
      setShowOverLayLoader(true);
      const predictionStatus = await proceedAnalyticsStatus();
      const status = predictionStatus?.data?.status?.toLowerCase();
      const description = predictionStatus?.data?.description;
      if (status === "completed") {
        clearInterval(intervalId.current);
        intervalId.current = null;
        setShowOverLayLoader(false);
        setCecWarningMessage("");
        const path = `${basePath}home?plantId=${plantId}`;
        navigate(path);
      } else if (status === "completed_with_warnings") {
        clearInterval(intervalId.current);
        intervalId.current = null;
        setShowOverLayLoader(false);
        setCecWarningMessage(description || "The analysis completed with warnings.");
        // Don't navigate - keep dialog open so user can see the warning
      } else if (
        status === "data_cleaning_in_progress" ||
        status === "cleaning_id_in_progress" ||
        status === "post_processing_in_progress"
      ) {
        setShowOverLayLoader(true);
      } else if (status === "error") {
        clearInterval(intervalId.current);
        intervalId.current = null;
        setShowOverLayLoader(false);
        createNotification(ERROR_NOTIFICATION, description, "");
      } else {
        setShowOverLayLoader(true); // Always keep loader ON for any other status
      }
    } catch (err) {
      console.log("Error is: ", err);
      clearInterval(intervalId.current);
      intervalId.current = null;
      setShowOverLayLoader(false);
    }
  };

  const deleteHandler = async (params) => {
    setRowData((prevRows) => prevRows.filter((row) => row.id !== params.id));
    setIsDisabled(false);
  };
  const handleUpdateRows = async () => {
    const payload = rowData.map((row) => ({
      cleaning_start_time: row.cleaning_start_time
        ? moment(row.cleaning_start_time).format("YYYY-MM-DD HH:mm:ss")
        : "",
      cleaning_end_time: row.cleaning_end_time
        ? moment(row.cleaning_end_time).format("YYYY-MM-DD HH:mm:ss")
        : "",
      cleaning_type: row.cleaning_type ? row.cleaning_type.toLowerCase() : "",
      is_tool_identified: row.is_tool_identified ? 1 : 0,
    }));
    try {
      setShowOverLayLoader(true);
      const response = await updatedCleanings(payload);
      if (response) {
        intervalId.current = setInterval(checkStatus, 5000);
        setIsDisabled(true);
      }
      // else{
      //   setShowOverLayLoader(false);
      // }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        ""
      );
    }
    // finally{
    //   setShowOverLayLoader(false);
    // }
  };

  const onClose = async (action) => {
    try {
      if (action === "CONFIRM") {
        await handleUpdateRows();
      } else if (action === "CANCEL") {
        setIdentifiedCECS(false);
      } else if (action === "RESET") {
        const path = `${basePath}home?plantId=${plantId}`;
        navigate(path);
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        ""
      );
      // setShowOverLayLoader(false);
      console.log("onClose", error);
    }
  };

  const onCellValueChanged = (params) => {
    const { data, colDef, oldValue, newValue, node } = params;
    let isValid = true;
    let errorMsg = "";
    if (
      colDef.field === "cleaning_start_time" ||
      colDef.field === "cleaning_end_time"
    ) {
      const start = moment(data.cleaning_start_time);
      const end = moment(data.cleaning_end_time);
      if (start.isValid() && end.isValid() && start.isAfter(end)) {
        isValid = false;
        errorMsg = t("analyseData.errorMsg");
      }
      // New validation: end of current CIP should not be after start of next CIP
      if (isValid) {
        // Find the index of the current row
        const currentIndex = rowData.findIndex((row) => row.id === data.id);
        if (currentIndex !== -1 && currentIndex < rowData.length - 1) {
          const nextCIP = rowData[currentIndex + 1];
          const currentEnd = moment(
            colDef.field === "cleaning_end_time"
              ? newValue
              : data.cleaning_end_time
          );
          const nextStart = moment(nextCIP.cleaning_start_time);
          if (
            currentEnd.isValid() &&
            nextStart.isValid() &&
            currentEnd.isAfter(nextStart)
          ) {
            isValid = false;
            errorMsg = t(
              "End of this CEC cannot be after the start of the next CEC."
            );
          }
        }
        // Also check previous CIP: start of current should not be before end of previous
        if (currentIndex > 0) {
          const prevCIP = rowData[currentIndex - 1];
          const prevEnd = moment(prevCIP.cleaning_end_time);
          const currentStart = moment(
            colDef.field === "cleaning_start_time"
              ? newValue
              : data.cleaning_start_time
          );
          if (
            prevEnd.isValid() &&
            currentStart.isValid() &&
            prevEnd.isAfter(currentStart)
          ) {
            isValid = false;
            errorMsg = t(
              "Start of this CEC cannot be before the end of the previous CEC."
            );
          }
        }
      }
    }
    if (!isValid) {
      createNotification(ERROR_NOTIFICATION, errorMsg, "");

      node.setDataValue(colDef.field, oldValue);
    } else {
      // update rowData state to reflect the change
      setRowData((prevRows) =>
        prevRows.map((row) => (row.id === data.id ? { ...row, ...data } : row))
      );
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    // Disable if any row has is_tool_identified === 0
    if (rowData.some((row) => row.is_tool_identified === 0)) {
      setIsDisabled(true);
    }
    // Optionally, you can keep your previous logic for enabling/disabling
    // else {
    //   setIsDisabled(false);
    // }
  }, [rowData]);

  return (
    <>
      {isIdentifiedCECS && (
        <DupontDialog
          open={isIdentifiedCECS}
          onClose={onClose}
          title={t("analyseData.identifiedCecTableTitle")}
          confirmBtnText={t("analyseData.confirmBtnText")}
          maxwidth="xl"
          minWidth={775}
          isOverlayLoader={showOverLayLoader}
          customHeight={"530px"}
          cancelBtnText={t("analyseData.cancelBtnText")}
          confirmDisabled={isDisabled}
          resetBtnText={t("analyseData.resetBtnText")}
        >
          {rowData && rowData.length > 0 ? (
            <>
              <div className="view-note marginBtm10">
                {t("analyseData.identifiedCecTableNote")}
              </div>
              <AgTable
                className="ag-table-identified-cec"
                columnDefs={colDefs}
                rowData={rowData}
                customHeight={"310px"}
                context={{
                  deleteHandler,
                }}
                hideExport
                onCellValueChanged={onCellValueChanged}
                getRowClass={(params) =>
                  params.data &&  !params.data.is_tool_identified
                    ? "disabled-row"
                    : ""
                }
              />
               {cecWarningMessage && (
                <div className="soft-warning-box marginTop10">
                  <img src={InfoIconGreen} alt="info icon" className="soft-warning-icon" />
                  <span className="soft-warning-text">{cecWarningMessage}</span>
                </div>
              )}
            </>
          ) : (
            <div className="lightGrayBorder no-cec-message">
              <div className="no-cec-message-title">
                <strong>{t("analyseData.noCecMessage.title")}</strong>
              </div>
              <p className="no-cec-message-paragraph">
                {t("analyseData.noCecMessage.autoAdd")}
              </p>
              <p className="no-cec-message-paragraph">
                {t("analyseData.noCecMessage.historic")}
              </p>
              <ul className="no-cec-message-list">
                <li className="no-cec-message-list-item">
                  {t("analyseData.noCecMessage.manualAdd")}
                </li>
                <li className="no-cec-message-list-item">
                  {t("analyseData.noCecMessage.fineTune")}
                  <ul>
                    {t("analyseData.noCecMessage.options", {
                      returnObjects: true,
                    }).map((option, idx) => (
                      <li className="no-cec-message-list-item-inner" key={idx}>
                        {option}
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
              <p
                className="no-cec-message-paragraph"
                style={{ marginTop: "18px" }}
              >
                {t("analyseData.noCecMessage.adjustments")}
              </p>
            </div>
          )}
        </DupontDialog>
      )}
    </>
  );
};

export default IdentifyCIP;
